<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import {Head, router, usePage} from "@inertiajs/vue3"
import CLoginBox from "@/Components/CLoginBox.vue"
import CLink from "@/Components/CLink.vue"
import EToast from "@/Components/EToast.vue"
import CDialog from "@/Components/CDialog.vue"
import PCart from "@/Layouts/Partials/PCart.vue"
import {eventBus} from "@/Services/eventBus.js";
import {useDisplay} from "vuetify";

const props = defineProps({
    cart: Boolean
})

const drawer = ref(false)
const dialog = ref()
const disableSearch = ref(false)
const page = usePage()
const urlSearchTerm = computed(() => page.props.searchTerm)
const searchTerm = ref(page.props.searchTerm)
const searchModal = ref(false)
const suggestedCategories = ref([])
const isLoading = ref(false)

let timeout, lastSearch = ''
const submitSearch = function () {

    lastSearch = searchTerm.value
    const filters = route().params

    delete filters.page; // sempre que for feita uma busca, resetamos para a primeira página
    delete filters.category; // sempre que fizermos uma busca, resetamos a categoria. todo - https://creativedesignbr.atlassian.net/browse/DEV-285
    delete filters.filters;

    if (typeof searchTerm.value === 'string' && searchTerm.value.trim() !== '') {
        filters.search = searchTerm.value
    }

    router.get(route('product.list', filters, {preserveState: true}), {}, {
            onSuccess: () => {
                searchModal.value = false
            },
        }
    )
}

// Função para buscar categorias sugeridas
const fetchSuggestedCategories = async (term) => {
    if (term.length < 3) {
        suggestedCategories.value = []
        return
    }

    isLoading.value = true
    try {
        const response = await axios.get(route('category.searchByTag'), {
            params: {term}
        })
        suggestedCategories.value = response.data.categories

    } catch (error) {
        console.error('Erro ao buscar categorias:', error)
        suggestedCategories.value = []
    } finally {
        isLoading.value = false
    }
}

const navigateToCategory = (category) => {
    if (!category || !category.slug) return

    router.get(route('product.category', category.slug), {}, {
        onBefore: () => {
            disableSearch.value = true
        },
        onFinish: () => {
            searchTerm.value = ''
            disableSearch.value = false
        }
    })
}

const handleOpenCart = () => {
    drawer.value = true
}

const display = useDisplay()
const appDisplay = display.width.value < 1025

onMounted(() => {
    eventBus.on('openCart', handleOpenCart)
})

// update searchTerm when url changes
watch(urlSearchTerm, function (search) {
    searchTerm.value = search || ''
})

watch(searchTerm, function (search) {
        if (timeout)
            clearTimeout(timeout)

        if (search?.length >= 3) {
            timeout = setTimeout(() => {
                fetchSuggestedCategories(search)

                if (search !== lastSearch) {
                }
            }, 200)
        } else {
            suggestedCategories.value = []
        }
    }
)
</script>

<template>
    <Head title="Catálogo"/>
    <v-app-bar clipped-left color="primary" style="position: absolute" :height="appDisplay ? 60 : 110">
        <template v-slot:prepend>
            <v-app-bar-title class="pa-5">
                <a v-if="page.props.portalId === 1" href="https://creativedesign.com.br/">
                    <v-img
                        width="150"
                        :height="appDisplay ? 50 : 75"
                        :src="page.props.logoUrl"
                        eager
                    />
                </a>
                <c-link v-else :href="route('product.list')">
                    <v-img
                        width="150"
                        :height="appDisplay ? 50 : 75"
                        :src="page.props.logoUrl"
                        eager
                    />
                </c-link>
            </v-app-bar-title>
        </template>
        <template v-slot:default>
            <v-btn v-if="$vuetify.display.mobile" icon @click="searchModal = true">
                <v-icon color="headerTextColor">mdi-magnify</v-icon>
            </v-btn>

            <div v-else class="w-100 d-flex justify-center align-baseline">
                <v-icon class="mr-1" color="headerTextColor" icon="mdi-magnify" @click="submitSearch"/>
                <v-autocomplete
                    v-model="searchTerm"
                    :disabled="disableSearch"
                    :items="suggestedCategories"
                    item-title="title"
                    item-value="id"
                    :search-input="searchTerm"
                    @update:search="value => searchTerm = value"
                    @update:model-value="navigateToCategory"
                    @keydown.enter="submitSearch"
                    :loading="isLoading"
                    no-filter
                    hide-no-data
                    return-object
                    hide-details
                    placeholder="Buscar produtos..."
                    variant="underlined"
                    density="comfortable"
                    color="headerTextColor"
                    base-color="headerTextColor"
                    class="mt-5"
                    style="max-width: 50vw;"
                />
            </div>

        </template>
        <template v-slot:append>
            <v-btn v-if="cart" @click.stop="drawer = !drawer" icon="" class="mr-8">
                <v-badge v-if="page.props.cartItems.length > 0" :content="page.props.cartItems.length"
                         color="headerTextColor" overlap>
                    <v-icon color="headerTextColor">mdi-cart</v-icon>
                </v-badge>
                <v-icon v-else color="headerTextColor">mdi-cart</v-icon>
            </v-btn>

            <v-btn id="loginBoxActivator" icon="mdi-account" color="headerTextColor" class="mr-8"/>
            <v-menu activator="#loginBoxActivator" :close-on-content-click="false">
                <v-sheet class="pa-5" width="400" rounded>
                    <c-login-box/>
                </v-sheet>
            </v-menu>
        </template>
    </v-app-bar>

    <!-- CARRINHO -->
    <p-cart v-model="drawer"/>

    <v-dialog v-model="searchModal" max-width="100vw">
        <v-card>
            <v-card-title>
                <v-form @submit.prevent="submitSearch" class="w-100 d-flex justify-center align-baseline">
                    <v-text-field v-model="searchTerm" placeholder="Digite sua busca" solo-inverted flat hide-details
                                  color="headerTextColor"/>
                </v-form>
            </v-card-title>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="searchModal = false">Fechar</v-btn>
                <v-btn color="primary" @click="submitSearch">Buscar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <c-dialog ref="dialog"/>
    <e-toast/>
</template>
